import * as IoC from 'typescript-ioc';
import { IApiClient, RequestContentType } from '../../infrastructure/api-client';
import { IEventRequestParams, IPaginationParams } from '../shared.dto';
import * as ASSETS_API from '../../constants/api/assets.constants';
import { IAssetFileDownloadUrlDTO, IAssetsDAO, IAssetsListItemDTO, IAssetsSpeakersListDTO, IAssetUrlDTO, ITitleAssetUrlDTO, ITitleObjectKeyDTO } from '../assets.dao';
import { APPLICATION_OCTET_STREAM, FILE_UPLOAD_UNSUPPORTED_TYPE_ERROR_MESSAGE } from '../../constants/file.constants';
import { getContentTypeFromName } from '../../infrastructure/utils/file.utils';
import { alertServerError } from '../../components/global/modal/alerts-host/alert-templates';
import { FileToS3DAO } from './file-to-s3.dao';
import { IAttendeeDetailsDTO } from '../attendees.dao';
import { LinkTargetValue } from '../../constants/common.constants';


class AssetsDAO extends IAssetsDAO {
    constructor(
        @IoC.Inject private readonly apiClient: IApiClient) {
        super();
    }

    public async getS3AssetUrlList(params: IEventRequestParams & { count: number; totalSize: number }): Promise<IAssetUrlDTO[]> {
        const response = await this.apiClient.get<IAssetUrlDTO[]>(ASSETS_API.GET_ASSETS_S3_URL, params);
        return response.parsedResponse;
    }

    public async getS3AssetUrl(params: IEventRequestParams, url: string): Promise<IAssetUrlDTO> {
        const response = await this.apiClient.get<IAssetUrlDTO>(url, params);
        return response.parsedResponse;
    }

    public uploadAssetToS3 = async (url: string, assetFile: File): Promise<IAssetUrlDTO> => {
        const hasKnownType = !!assetFile.type && assetFile.type !== APPLICATION_OCTET_STREAM;
        const fileType = hasKnownType ? assetFile.type : getContentTypeFromName(assetFile);
        if (!fileType) {
            throw new Error(FILE_UPLOAD_UNSUPPORTED_TYPE_ERROR_MESSAGE(assetFile.name));
        }
        const response = await this.apiClient.put<IAssetUrlDTO>(url, assetFile, {
            ContentType: fileType as RequestContentType
        });
        return response.parsedResponse;
    };

    public async getAssets(params: IEventRequestParams): Promise<IAssetsListItemDTO> {
        const response = await this.apiClient.get<IAssetsListItemDTO>(ASSETS_API.ASSETS_URL, params);
        return response.parsedResponse;
    }

    public async updateAssets(params: IEventRequestParams, files: File[]): Promise<IAssetsListItemDTO> {
        let uploadingFilesErrors = [];
        const totalSize = files.reduce((total, file) => {
            total += file.size;
            return total;
        }, 0);

        const assertUrls = await this.getS3AssetUrlList({
            accountId: params.accountId,
            eventId: params.eventId,
            count: files.length,
            totalSize: totalSize
        });
        const tmpMergedDataFiles = files.map((item, index) =>
        ({
            file: item,
            ...assertUrls[index],
        })
        );
        let resultMergedDataFiles = [];
        const batches = Array(files.length).fill(this.uploadAssetToS3);
        await Promise.allSettled(batches.map(async (f, index) => await f(tmpMergedDataFiles[index].assetUrl, tmpMergedDataFiles[index].file)))
        .then(results => {
            results.forEach((result, index) => {
              if (result.status === 'fulfilled') {
                resultMergedDataFiles.push(tmpMergedDataFiles[index]);
              } else if (result.status === 'rejected') {
                /* istanbul ignore next */
                uploadingFilesErrors.push(result?.reason?.rawResponse || result?.reason?.message || 'Unknown failure reason');
              }
            });
          });

        if (uploadingFilesErrors.length) alertServerError('Error', uploadingFilesErrors.join('\n'));

        const filesData = resultMergedDataFiles.map(val => {
            return {
                title: val.file.name,
                objectKey: val.objectKey,
            };
        });

        const response = await this.apiClient.put<IAssetsListItemDTO>(ASSETS_API.ASSETS_URL, { ...params, files: filesData });
        return response.parsedResponse;
    }


    public async updateProfileImage(params: IEventRequestParams & { attendeeId: number },
        profileImage: ITitleObjectKeyDTO): Promise<IAttendeeDetailsDTO> {
        const queryParams = {
            ...params,
            objectKey: profileImage.objectKey,
            title: profileImage.title,
            ...(profileImage.assetMetadataId && { assetId: profileImage.assetMetadataId})
        };
        const response = await this.apiClient.put<IAttendeeDetailsDTO>(
            ASSETS_API.ASSETS_UPLOAD_PROFILE_IMAGE_URL,
            queryParams,
        );
        return response.parsedResponse;
    }

    public async addProfileImageWithS3(params: IEventRequestParams & { attendeeId: number }, file: File): Promise<IAttendeeDetailsDTO> {
        const fileParams = await this.getS3AssetUrl({
                accountId: params.accountId,
                eventId: params.eventId,
            },
            FileToS3DAO.paths['attendee']
        );

        await this.uploadAssetToS3(fileParams.assetUrl, file);
        return await this.updateProfileImage(params , { objectKey: fileParams.objectKey, title: file.name });
    }


    public async getAssetFile(url: string, fileName: string): Promise<void> {
        const a = document.createElement('a');
        a.download = fileName;
        a.href = url;
        a.target = LinkTargetValue.BLANK;
        document.body.appendChild(a);
        a.click();
        a.remove();
    }

    public async getAssetsSpeakers(params: IEventRequestParams | IPaginationParams): Promise<IAssetsSpeakersListDTO> {
        const response = await this.apiClient.get<IAssetsSpeakersListDTO>(ASSETS_API.ASSETS_SPEAKERS_URL, params);
        return response.parsedResponse;
    }

    public async deleteAssetFile(params: IEventRequestParams & { assetMetadataId?: number }) : Promise<void> {
        await this.apiClient.delete(ASSETS_API.DELETE_ASSET, params);
    }

    public async deleteSpeakerAssetFile(params: IEventRequestParams & { attendeeId?: number }): Promise<void> {
        await this.apiClient.put(ASSETS_API.ASSETS_UPLOAD_PROFILE_IMAGE_URL, params);
    }

    public async getAssetFileDownloadUrl(params: IEventRequestParams & { assetMetadataId: number }): Promise<IAssetFileDownloadUrlDTO> {
        const response = await this.apiClient.get<IAssetFileDownloadUrlDTO>(ASSETS_API.ASSET_FILE_DOWNLOAD_URL, params);
        return response.parsedResponse;
    }

    public async createAssetLink(params: IEventRequestParams & { assetMetadataId: number }): Promise<ITitleAssetUrlDTO> {
        const response = await this.apiClient.get<ITitleAssetUrlDTO>(ASSETS_API.CREATE_ASSET_LINK, params);
        return response.parsedResponse;
    }

    public async deleteAssetLink(params: IEventRequestParams & { assetMetadataId: number }): Promise<void> {
        await this.apiClient.delete<void>(ASSETS_API.DELETE_ASSET_LINK, params);
    }
}

export { AssetsDAO };
